import type { Shape } from '@pqina/pintura';

export type CanvasPresetId =
  | 'square'
  | 'portrait'
  | 'story'
  | 'landscape'
  | 'hd';

export type CanvasDefinition = {
  preset: CanvasPresetId | 'custom';
  width: number;
  height: number;
  backgroundColor: string;
};

export type OverlayLayerKind = 'text' | 'shape' | 'image' | 'background';
export type TemplateShapeType =
  | 'rectangle'
  | 'ellipse'
  | 'triangle'
  | 'straightLine'
  | 'wavyLine'
  | 'arrow'
  | 'diamond'
  | 'star';

/**
 * A template holds at most one `background` layer. It is pinned to the bottom
 * of the stack and paints its `fillColor` behind an optional image. Its frame
 * can be moved, resized, and rotated like other layers.
 */
export type OverlayLayer = {
  id: string;
  name: string;
  kind: OverlayLayerKind;
  locked: boolean;
  visible: boolean;
  /** Clockwise degrees. The mapper converts this to Pintura radians. */
  rotation?: number;
  position: { x: number; y: number };
  size: { width: number; height: number };
  text?: string;
  /** Locks text editing and always requires the containing layer to be locked. */
  textLocked?: boolean;
  typography?: {
    fontFamily: string;
    fontSize: number;
    fontWeight: 'normal' | 'bold';
    fontStyle: 'normal' | 'italic';
    textAlign: 'left' | 'center' | 'right';
    color: string;
    lineHeight: number;
    letterSpacing: number;
  };
  fillColor?: string;
  cornerRadius?: number;
  shapeType?: TemplateShapeType;
  image?: {
    src?: string;
    /** Persistent media UUID when the image was uploaded by the host app. */
    uuid?: string;
    fit: 'cover' | 'contain' | 'stretch' | 'natural';
    /** Keeps the image frame proportional while it is resized on the canvas. */
    lockAspectRatio?: boolean;
    opacity?: number;
    borderColor?: string;
    borderWidth?: number;
  };
};

export type OverlayDefinition = {
  version: 1;
  id: string;
  name: string;
  canvas: CanvasDefinition;
  layers: OverlayLayer[];
};

export const OVERLAY_EDITOR_THUMBNAIL_WIDTH = 600;

export const canvasPresets: Array<{
  id: CanvasPresetId;
  label: string;
  width: number;
  height: number;
}> = [
  { id: 'square', label: 'Square', width: 1080, height: 1080 },
  { id: 'portrait', label: 'Portrait', width: 1080, height: 1350 },
  { id: 'story', label: 'Story / Reel', width: 1080, height: 1920 },
  { id: 'landscape', label: 'Landscape', width: 1200, height: 628 },
  { id: 'hd', label: 'HD', width: 1920, height: 1080 }
];

/**
 * The editor opens immediately; 4:5 portrait is the product default for a
 * newly created template. Canvas size can subsequently be changed in Layers.
 */
export const createDefaultOverlay = (
  id: string,
  name = ''
): OverlayDefinition => ({
  version: 1,
  id,
  name,
  canvas: {
    preset: 'portrait',
    width: 1080,
    height: 1350,
    backgroundColor: 'transparent'
  },
  layers: []
});

export type OverlayPinturaShape = Shape & Record<string, unknown>;

const hexToColor = (value: string) =>
  [1, 3, 5].map((index) => parseInt(value.slice(index, index + 2), 16) / 255);

const colorToHex = (value: unknown, fallback: string) =>
  Array.isArray(value) && value.length >= 3
    ? `#${value
        .slice(0, 3)
        .map((channel) =>
          Math.round(Number(channel) * 255)
            .toString(16)
            .padStart(2, '0')
        )
        .join('')}`
    : fallback;

/**
 * Largest raster we will ask a browser to make for one shape. A shape drawn
 * bigger than this is rare, and the softening at that size costs less than the
 * memory a full-size texture would take.
 */
const MAXIMUM_SHAPE_IMAGE_SIZE = 2048;

/**
 * Renders a shape as an SVG sized to the box it will occupy.
 *
 * These are handed to Pintura as `backgroundImage`, and it rasterises them
 * once at whatever size the SVG claims to be. An SVG carrying only a `viewBox`
 * claims 150x150 — so every shape larger than that, which is most of them on a
 * 1080px canvas, was a 150px bitmap stretched over the layer. That is what
 * made triangles and stars look pixelated.
 *
 * `preserveAspectRatio="none"` keeps the geometry that `backgroundSize` was
 * already producing: the artwork fills the layer box in both directions rather
 * than being letterboxed inside it.
 */
/**
 * The drawings are rebuilt only when they would actually differ.
 *
 * A shape's picture depends on its outline, its colour and its box — never on
 * where that box sits. Building it afresh each time the layer object changed
 * meant a new data URL on every pointer move of a drag, and a new data URL is
 * an image Pintura has to decode again. Dragging a couple of shapes grew
 * steadily slower for no visible change at all.
 */
const shapeImageCache = new Map<string, string>();

/** Enough for the shapes on a canvas at the handful of sizes a session uses. */
const SHAPE_IMAGE_CACHE_LIMIT = 64;

const shapeImage = (
  viewBox: string,
  artwork: string,
  size: { width: number; height: number }
) => {
  const scale = Math.min(
    1,
    MAXIMUM_SHAPE_IMAGE_SIZE / Math.max(size.width, size.height, 1)
  );
  const width = Math.max(1, Math.round(size.width * scale));
  const height = Math.max(1, Math.round(size.height * scale));

  const key = `${viewBox}|${width}x${height}|${artwork}`;
  const cached = shapeImageCache.get(key);
  if (cached) return cached;

  const image = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}" width="${width}" height="${height}" preserveAspectRatio="none">${artwork}</svg>`
  )}`;

  if (shapeImageCache.size >= SHAPE_IMAGE_CACHE_LIMIT) {
    // Insertion order, so this drops the one least recently added.
    const oldest = shapeImageCache.keys().next().value;
    if (oldest !== undefined) shapeImageCache.delete(oldest);
  }
  shapeImageCache.set(key, image);
  return image;
};

type ShapeImageSize = { width: number; height: number };

const triangleImage = (fillColor: string, size: ShapeImageSize) =>
  shapeImage('0 0 100 100', `<path fill="${fillColor}" d="M50 0 100 100H0z"/>`, size);

const wavyLineImage = (strokeColor: string, size: ShapeImageSize) =>
  shapeImage(
    '0 0 100 24',
    `<path fill="none" stroke="${strokeColor}" stroke-linecap="round" stroke-width="6" d="M3 12c12-16 22 16 34 0s22 16 34 0 17 0 26 0"/>`,
    size
  );

const arrowImage = (fillColor: string, size: ShapeImageSize) =>
  shapeImage(
    '0 0 160 80',
    `<path fill="${fillColor}" d="M0 32h96V8l64 32-64 32V48H0z"/>`,
    size
  );

const diamondImage = (fillColor: string, size: ShapeImageSize) =>
  shapeImage(
    '0 0 100 100',
    `<path fill="${fillColor}" d="M50 0l50 50-50 50L0 50z"/>`,
    size
  );

const starImage = (fillColor: string, size: ShapeImageSize) =>
  shapeImage(
    '0 0 100 100',
    `<path fill="${fillColor}" d="m50 2 11.2 34.5H97L68 57l11.2 34.5L50 70 20.8 91.5 32 57 3 36.5h35.8z"/>`,
    size
  );

/**
 * This editor uses only Pintura annotations and always passes canvas pixels.
 * Crop and decoration coordinate systems are intentionally out of scope and
 * can therefore never leak into the template definition.
 */
export const toPinturaShape = (layer: OverlayLayer): OverlayPinturaShape => {
  const base = {
    id: layer.id,
    opacity:
      layer.visible && layer.kind === 'image'
        ? layer.image?.opacity ?? 1
        : layer.visible
        ? 1
        : 0,
    rotation: ((layer.rotation ?? 0) * Math.PI) / 180,
    // The application sidebar owns all edits. The canvas is deliberately only a
    // selection and positioning surface for unlocked layers.
    disableStyle: true,
    disableInput: true,
    disableNewline: false,
    disableTextLayout: true,
    disableRemove: true,
    disableDuplicate: true,
    disableReorder: true,
    disableFlip: true,
    disableResize: layer.locked,
    disableManipulate: layer.locked,
    disableMove: layer.locked,
    disableSelect: !layer.visible || layer.locked
  };
  const rectangle = {
    x: layer.position.x,
    y: layer.position.y,
    width: layer.size.width,
    height: layer.size.height
  };

  if (layer.kind === 'background') {
    const isEmpty =
      (!layer.fillColor || layer.fillColor === 'transparent') &&
      !layer.image?.src;
    return {
      ...base,
      ...rectangle,
      backgroundColor:
        layer.fillColor && layer.fillColor !== 'transparent'
          ? hexToColor(layer.fillColor)
          : [0, 0, 0, 0],
      backgroundImage: layer.image?.src,
      backgroundSize:
        layer.image?.fit === 'stretch'
          ? { width: layer.size.width, height: layer.size.height }
          : layer.image?.src
          ? layer.image.fit ?? 'cover'
          : undefined,
      aspectRatio: layer.image?.lockAspectRatio
        ? layer.size.width / layer.size.height
        : undefined,
      disableSelect: !layer.visible || layer.locked || isEmpty,
      disableMove: layer.locked || isEmpty,
      disableResize: layer.locked || isEmpty,
      disableManipulate: layer.locked || isEmpty
    };
  }

  if (layer.kind === 'shape' && layer.shapeType === 'ellipse') {
    return {
      ...base,
      x: layer.position.x + layer.size.width / 2,
      y: layer.position.y + layer.size.height / 2,
      rx: layer.size.width / 2,
      ry: layer.size.height / 2,
      backgroundColor: hexToColor(layer.fillColor ?? '#3359c7')
    };
  }

  if (
    layer.kind === 'shape' &&
    (layer.shapeType === 'triangle' ||
      layer.shapeType === 'wavyLine' ||
      layer.shapeType === 'arrow' ||
      layer.shapeType === 'diamond' ||
      layer.shapeType === 'star')
  ) {
    return {
      ...base,
      ...rectangle,
      backgroundColor: [0, 0, 0, 0],
      backgroundImage: (() => {
        const fillColor = layer.fillColor ?? '#3359c7';
        const size = layer.size;
        if (layer.shapeType === 'triangle') return triangleImage(fillColor, size);
        if (layer.shapeType === 'wavyLine') return wavyLineImage(fillColor, size);
        if (layer.shapeType === 'arrow') return arrowImage(fillColor, size);
        if (layer.shapeType === 'diamond') return diamondImage(fillColor, size);
        return starImage(fillColor, size);
      })(),
      backgroundSize: { width: layer.size.width, height: layer.size.height }
    };
  }

  return {
    ...base,
    ...rectangle,
    ...(layer.kind === 'text'
      ? {
          text: layer.text,
          ...layer.typography,
          lineHeight: `${(layer.typography?.lineHeight ?? 1.1) * 100}%`,
          color: hexToColor(layer.typography?.color ?? '#000000')
        }
      : {}),
    ...(layer.kind === 'shape'
      ? {
          backgroundColor: hexToColor(layer.fillColor ?? '#3359c7'),
          cornerRadius: layer.cornerRadius ?? 0
        }
      : {}),
    ...(layer.kind === 'image'
      ? {
          backgroundImage: layer.image?.src,
          backgroundColor: layer.image?.src ? undefined : [0, 0, 0, 0],
          backgroundSize:
            layer.image?.fit === 'stretch'
              ? { width: layer.size.width, height: layer.size.height }
              : layer.image?.fit === 'natural'
              ? undefined
              : layer.image?.fit ?? 'cover',
          aspectRatio: (layer.image?.lockAspectRatio ?? true)
            ? layer.size.width / layer.size.height
            : undefined,
          strokeColor:
            layer.image?.borderWidth && layer.image.borderWidth > 0
              ? hexToColor(layer.image.borderColor ?? '#000000')
              : undefined,
          strokeWidth: layer.image?.borderWidth ?? 0,
          cornerRadius: layer.cornerRadius ?? 0
        }
      : {}),
    ...base
  };
};

const percentageToCanvasValue = (
  value: unknown,
  total: number,
  fallback: number
) => {
  if (typeof value === 'string' && value.endsWith('%')) {
    const percentage = Number.parseFloat(value);
    return Number.isFinite(percentage) ? (percentage / 100) * total : fallback;
  }
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
};

const pinturaLineHeightToMultiplier = (
  value: unknown,
  fontSize: number,
  fallback: number
) => {
  if (typeof value === 'string' && value.endsWith('%')) {
    const percentage = Number.parseFloat(value);
    return Number.isFinite(percentage) ? percentage / 100 : fallback;
  }
  return typeof value === 'number' && fontSize > 0
    ? value / fontSize
    : fallback;
};

export const pinturaShapeToLayerPatch = (
  shape: Shape,
  canvas: CanvasDefinition,
  previousLayer: OverlayLayer
): Partial<OverlayLayer> => {
  const source = shape as OverlayPinturaShape;
  const ellipse =
    typeof source.rx === 'number' && typeof source.ry === 'number';
  const points = Array.isArray(source.points) ? source.points : undefined;
  const pathBounds = points?.reduce(
    (bounds, point) => ({
      left: Math.min(bounds.left, Number(point.x)),
      top: Math.min(bounds.top, Number(point.y)),
      right: Math.max(bounds.right, Number(point.x)),
      bottom: Math.max(bounds.bottom, Number(point.y))
    }),
    { left: Infinity, top: Infinity, right: -Infinity, bottom: -Infinity }
  );
  const x = ellipse
    ? Number(source.x) - Number(source.rx)
    : pathBounds && Number.isFinite(pathBounds.left)
    ? pathBounds.left
    : source.x;
  const y = ellipse
    ? Number(source.y) - Number(source.ry)
    : pathBounds && Number.isFinite(pathBounds.top)
    ? pathBounds.top
    : source.y;
  const width = ellipse
    ? Number(source.rx) * 2
    : pathBounds && Number.isFinite(pathBounds.right)
    ? pathBounds.right - pathBounds.left
    : source.width;
  const height = ellipse
    ? Number(source.ry) * 2
    : pathBounds && Number.isFinite(pathBounds.bottom)
    ? pathBounds.bottom - pathBounds.top
    : source.height;
  const patch: Partial<OverlayLayer> = {
    rotation:
      typeof source.rotation === 'number' && Number.isFinite(source.rotation)
        ? (source.rotation * 180) / Math.PI
        : previousLayer.rotation ?? 0,
    position: {
      x: percentageToCanvasValue(x, canvas.width, previousLayer.position.x),
      y: percentageToCanvasValue(y, canvas.height, previousLayer.position.y)
    },
    size: {
      width: percentageToCanvasValue(
        width,
        canvas.width,
        previousLayer.size.width
      ),
      height: percentageToCanvasValue(
        height,
        canvas.height,
        previousLayer.size.height
      )
    }
  };
  if (typeof source.text === 'string') {
    patch.text = source.text;
    patch.typography = {
      fontFamily: String(source.fontFamily ?? 'Arial'),
      fontSize: Number(source.fontSize ?? 48),
      fontWeight: source.fontWeight === 'bold' ? 'bold' : 'normal',
      fontStyle: source.fontStyle === 'italic' ? 'italic' : 'normal',
      textAlign:
        source.textAlign === 'center' || source.textAlign === 'right'
          ? source.textAlign
          : 'left',
      color: colorToHex(source.color, '#000000'),
      lineHeight: pinturaLineHeightToMultiplier(
        source.lineHeight,
        Number(source.fontSize ?? 48),
        1.1
      ),
      letterSpacing: Number(source.letterSpacing ?? 0)
    };
  }
  return patch;
};

export const resizeOverlayCanvas = (
  template: OverlayDefinition,
  width: number,
  height: number,
  preset: CanvasDefinition['preset']
): OverlayDefinition => {
  const scaleX = width / template.canvas.width;
  const scaleY = height / template.canvas.height;
  return {
    ...template,
    canvas: { ...template.canvas, preset, width, height },
    layers: template.layers.map((layer) => ({
      ...layer,
      position: {
        x: layer.position.x * scaleX,
        y: layer.position.y * scaleY
      },
      size: {
        width: layer.size.width * scaleX,
        height: layer.size.height * scaleY
      }
    }))
  };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const isHexColor = (value: unknown): value is string =>
  typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value);

const isCanvasBackground = (value: unknown): value is string =>
  value === 'transparent' || isHexColor(value);

const defaultTypography = (): NonNullable<OverlayLayer['typography']> => ({
  fontFamily: 'Arial',
  fontSize: 48,
  fontWeight: 'normal',
  fontStyle: 'normal',
  textAlign: 'left',
  color: '#000000',
  lineHeight: 1.1,
  letterSpacing: 0
});

const parseGeometry = (value: Record<string, unknown>, index: number) => {
  if (
    !isRecord(value.position) ||
    !isRecord(value.size) ||
    !isFiniteNumber(value.position.x) ||
    !isFiniteNumber(value.position.y) ||
    !isFiniteNumber(value.size.width) ||
    !isFiniteNumber(value.size.height) ||
    value.size.width <= 0 ||
    value.size.height <= 0
  ) {
    throw new Error(`Layer ${index + 1} has invalid geometry.`);
  }
  return {
    position: { x: value.position.x, y: value.position.y },
    size: { width: value.size.width, height: value.size.height }
  };
};

const parseLayer = (
  value: unknown,
  index: number,
  canvas: CanvasDefinition
): OverlayLayer => {
  if (!isRecord(value))
    throw new Error(`Layer ${index + 1} must be an object.`);
  const kind = value.kind;
  if (
    kind !== 'text' &&
    kind !== 'shape' &&
    kind !== 'image' &&
    kind !== 'background'
  ) {
    throw new Error(`Layer ${index + 1} has an unsupported kind.`);
  }
  if (typeof value.id !== 'string') {
    throw new Error(`Layer ${index + 1} has an invalid id.`);
  }
  const layer: OverlayLayer = {
    id: value.id,
    name:
      typeof value.name === 'string'
        ? value.name
        : kind === 'text'
        ? 'Text layer'
        : kind === 'image'
        ? 'Image layer'
        : kind === 'background'
        ? 'Background'
        : 'Shape layer',
    kind,
    locked: value.locked === true,
    visible: value.visible !== false,
    rotation: isFiniteNumber(value.rotation) ? value.rotation : 0,
    // Older background definitions did not need to persist geometry. Keep
    // those files compatible by starting them at the canvas bounds.
    ...(kind === 'background'
      ? isRecord(value.position) && isRecord(value.size)
        ? parseGeometry(value, index)
        : {
            position: { x: 0, y: 0 },
            size: { width: canvas.width, height: canvas.height }
          }
      : parseGeometry(value, index))
  };
  if (kind === 'text') {
    layer.textLocked = value.textLocked === true;
    if (layer.textLocked) layer.locked = true;
    const typography = isRecord(value.typography) ? value.typography : {};
    layer.text = typeof value.text === 'string' ? value.text : '';
    layer.typography = {
      ...defaultTypography(),
      fontFamily:
        typeof typography.fontFamily === 'string'
          ? typography.fontFamily
          : 'Arial',
      fontSize: isFiniteNumber(typography.fontSize) ? typography.fontSize : 48,
      fontWeight: typography.fontWeight === 'bold' ? 'bold' : 'normal',
      fontStyle: typography.fontStyle === 'italic' ? 'italic' : 'normal',
      textAlign:
        typography.textAlign === 'center' || typography.textAlign === 'right'
          ? typography.textAlign
          : 'left',
      color: isHexColor(typography.color) ? typography.color : '#000000',
      lineHeight: isFiniteNumber(typography.lineHeight)
        ? typography.lineHeight
        : 1.1,
      letterSpacing: isFiniteNumber(typography.letterSpacing)
        ? typography.letterSpacing
        : 0
    };
  }
  if (kind === 'shape') {
    layer.fillColor = isHexColor(value.fillColor) ? value.fillColor : '#3359c7';
    layer.cornerRadius = isFiniteNumber(value.cornerRadius)
      ? Math.max(0, value.cornerRadius)
      : 0;
    layer.shapeType =
      value.shapeType === 'ellipse' ||
      value.shapeType === 'triangle' ||
      value.shapeType === 'straightLine' ||
      value.shapeType === 'wavyLine' ||
      value.shapeType === 'arrow' ||
      value.shapeType === 'diamond' ||
      value.shapeType === 'star'
        ? value.shapeType
        : 'rectangle';
  }
  if (kind === 'image') {
    const image = isRecord(value.image) ? value.image : {};
    layer.image = {
      src: typeof image.src === 'string' ? image.src : undefined,
      uuid: typeof image.uuid === 'string' ? image.uuid : undefined,
      // "natural" was a short-lived editor option; normalize old templates to
      // the closest supported, visible fit mode.
      fit:
        image.fit === 'cover' || image.fit === 'stretch'
          ? image.fit
          : 'contain',
      ...(typeof image.lockAspectRatio === 'boolean'
        ? { lockAspectRatio: image.lockAspectRatio }
        : {}),
      opacity: isFiniteNumber(image.opacity)
        ? Math.min(1, Math.max(0, image.opacity))
        : 1,
      borderColor: isHexColor(image.borderColor)
        ? image.borderColor
        : '#000000',
      borderWidth: isFiniteNumber(image.borderWidth)
        ? Math.max(0, image.borderWidth)
        : 0
    };
    layer.cornerRadius = isFiniteNumber(value.cornerRadius)
      ? Math.max(0, value.cornerRadius)
      : 0;
  }
  if (kind === 'background') {
    const image = isRecord(value.image) ? value.image : {};
    layer.fillColor = isCanvasBackground(value.fillColor)
      ? value.fillColor
      : '#ffffff';
    layer.image = {
      src: typeof image.src === 'string' ? image.src : undefined,
      uuid: typeof image.uuid === 'string' ? image.uuid : undefined,
      fit:
        image.fit === 'contain' ||
        image.fit === 'stretch' ||
        image.fit === 'cover'
          ? image.fit
          : 'cover',
      ...(typeof image.lockAspectRatio === 'boolean'
        ? { lockAspectRatio: image.lockAspectRatio }
        : {})
    };
  }
  return layer;
};

export const hasBackgroundLayer = (layers: OverlayLayer[]) =>
  layers.some((layer) => layer.kind === 'background');

/**
 * Converges any saved or imported template on the background rules: there is
 * at most one background and it sits at the bottom of the stack.
 */
const withBackgroundInvariants = (layers: OverlayLayer[]): OverlayLayer[] => {
  const backgrounds = layers.filter((layer) => layer.kind === 'background');
  if (backgrounds.length > 1) {
    throw new Error('An overlay can only contain one background layer.');
  }
  const [background] = backgrounds;
  if (!background) return layers;
  return [background, ...layers.filter((layer) => layer.kind !== 'background')];
};

/**
 * Produces the canonical, versioned model used for persistence. It accepts
 * versionless early POC templates and fills their documented defaults.
 */
export const normalizeOverlay = (value: unknown): OverlayDefinition => {
  if (
    !isRecord(value) ||
    !isRecord(value.canvas) ||
    !Array.isArray(value.layers)
  ) {
    throw new Error('This is not a valid overlay definition.');
  }
  if (value.version !== undefined && value.version !== 1) {
    throw new Error('This overlay version is not supported.');
  }
  if (
    typeof value.id !== 'string' ||
    typeof value.name !== 'string' ||
    !isFiniteNumber(value.canvas.width) ||
    !isFiniteNumber(value.canvas.height) ||
    value.canvas.width <= 0 ||
    value.canvas.height <= 0
  ) {
    throw new Error('This overlay has invalid canvas metadata.');
  }
  const preset = value.canvas.preset;
  const validPreset =
    preset === 'square' ||
    preset === 'portrait' ||
    preset === 'story' ||
    preset === 'landscape' ||
    preset === 'hd' ||
    preset === 'custom';
  if (!validPreset)
    throw new Error('This overlay has an unsupported canvas preset.');

  const canvas: CanvasDefinition = {
    preset,
    width: value.canvas.width,
    height: value.canvas.height,
    backgroundColor: isCanvasBackground(value.canvas.backgroundColor)
      ? value.canvas.backgroundColor
      : 'transparent'
  };
  const layers = withBackgroundInvariants(
    value.layers.map((layer, index) => parseLayer(layer, index, canvas))
  );
  if (new Set(layers.map((layer) => layer.id)).size !== layers.length) {
    throw new Error('Overlay layer IDs must be unique.');
  }
  return { version: 1, id: value.id, name: value.name, canvas, layers };
};

export const serializeOverlay = (template: OverlayDefinition) =>
  JSON.stringify(normalizeOverlay(template), null, 2);

export const parseOverlay = (json: string): OverlayDefinition =>
  normalizeOverlay(JSON.parse(json) as unknown);

export const createLayer = (
  kind: OverlayLayerKind,
  canvas: CanvasDefinition,
  shapeType: TemplateShapeType = 'rectangle'
): OverlayLayer => {
  const id = `layer-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const base = { id, locked: false, visible: true, rotation: 0 };
  if (kind === 'background') {
    return {
      ...base,
      kind,
      name: 'Background',
      position: { x: 0, y: 0 },
      size: { width: canvas.width, height: canvas.height },
      fillColor: '#ffffff',
      image: { fit: 'cover' }
    };
  }
  if (kind === 'text') {
    return {
      ...base,
      kind,
      name: 'Text layer',
      position: { x: canvas.width * 0.1, y: canvas.height * 0.12 },
      size: { width: canvas.width * 0.8, height: 150 },
      text: 'Text',
      textLocked: false,
      typography: {
        fontFamily: 'Arial',
        fontSize: 56,
        fontWeight: 'normal',
        fontStyle: 'normal',
        textAlign: 'left',
        color: '#000000',
        lineHeight: 1.1,
        letterSpacing: 0
      }
    };
  }
  if (kind === 'image') {
    return {
      ...base,
      kind,
      name: 'Image layer',
      position: { x: canvas.width * 0.1, y: canvas.height * 0.35 },
      size: { width: canvas.width * 0.8, height: canvas.height * 0.35 },
      image: {
        fit: 'contain',
        lockAspectRatio: true,
        opacity: 1,
        borderColor: '#000000',
        borderWidth: 0
      },
      cornerRadius: 0
    };
  }
  return {
    ...base,
    kind,
    name: 'Shape layer',
    position: { x: canvas.width * 0.2, y: canvas.height * 0.25 },
    size:
      shapeType === 'straightLine'
        ? {
            width: canvas.width * 0.6,
            height: Math.max(8, canvas.height * 0.01)
          }
        : shapeType === 'wavyLine' || shapeType === 'arrow'
        ? { width: canvas.width * 0.6, height: canvas.height * 0.12 }
        : { width: canvas.width * 0.6, height: canvas.height * 0.2 },
    fillColor: '#3359c7',
    cornerRadius: 0,
    shapeType
  };
};
